/**
 * Today (B-33, B-34) — the first tab, and only ever about today.
 *
 * It used to be the whole ledger of every gym day, which made the first screen answer
 * "what have I ever done?" when the question a phone in a gym is asking is "what am I
 * doing now?". The ledger lives under the chart on Progress now, where the picture of it
 * already was.
 *
 * B-34 turns it from a read-only mirror into the working surface: today's day of the
 * plan, or else last time on this weekday (`todaySuggestion`, B-49), sets prefilled, a checkbox on
 * every set. Ticking writes that one set through a transaction (`addSet`); unticking
 * deletes it (`removeSet`). Nothing here is a second editor — `/day/:date` still owns
 * moving a day, changing its bodyweight, and everything shorthand cannot express (D-09).
 *
 * The headline is today's own score and nothing else (D-89). It was the last seven days
 * and the delta on the seven before — a real number, but a different question from the
 * one this screen exists to answer, and the only thing on it that made you look backwards
 * while standing in the gym. Every week-scale figure lives on Progress now, where a range
 * selector says what it is scoped to. It is also no longer set in `--t-display`: a score
 * four inches tall was sized when it was the whole screen, and it now sits above a list
 * of sets it should not shout over.
 *
 * B-57 (D-108) shows it one exercise at a time. The list was one long scroll of cards —
 * a set row is ~120px, so five exercises ran to several screens — read on a phone held
 * between sets. Each card is now at least as tall as the space between a pinned bar and
 * the tab bar, and the page snaps vertically so a flick lands the next card whole. The bar
 * carries what used to stack above the list: the score (or a short form of the
 * suggestion), the PT switch and "+ Add". The suggestion's full sentence and the plan
 * nudge moved to a small footer below the last card.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type {
  CatalogExercise,
  ExerciseSet,
  LoggedExercise,
  TodaySuggestion,
  Workout,
} from "@altrtrack/core";
import {
  DEFAULT_BODYWEIGHT_KG,
  dayOfWeek,
  exerciseById,
  formatNumber,
  plural,
  scoreWorkout,
  suggestedExercises,
  todaySuggestion,
} from "@altrtrack/core";
import { localToday, usePlan, useProfile, useWorkouts } from "../data.js";
import { mergeToday, type TodayRow } from "../today.js";
import { addSet, removeSet, setPt, updateSet } from "../write.js";
import { ExercisePicker } from "../components/ExercisePicker.js";
import { ExerciseEditor } from "../components/ExerciseEditor.js";

/**
 * A logged exercise whose catalogId is not in the catalog scores 0 (D-34). The editor
 * has to render it anyway rather than crash — the data exists, and this screen is how
 * someone would fix it. Same constant `DayPage` uses, re-declared rather than shared
 * because there is nowhere both screens' modules import from without one importing the
 * other.
 */
const UNKNOWN_EXERCISE = { bodyweightFactor: 0, measure: "reps" } as const;

/** One object, so the memo below it is not invalidated by a fresh literal on every render. */
const NOTHING_OFFERED: TodaySuggestion = { kind: "empty" };

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function TodayPage({ uid }: { uid: string }) {
  const profile = useProfile(uid);
  const workouts = useWorkouts(uid);
  const plan = usePlan(uid);
  const [picking, setPicking] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [ptBusy, setPtBusy] = useState(false);
  /** Edits to sets that are proposed but not yet ticked. Nothing here is stored. */
  const [edits, setEdits] = useState<ReadonlyMap<string, ExerciseSet>>(new Map());
  /** Exercises picked this session that are neither stored nor suggested. */
  const [added, setAdded] = useState<CatalogExercise[]>([]);
  /**
   * Extra proposed sets from "Add a set" on a row that already has `done` checkboxes
   * (I-3 part 2). Keyed by catalogId. Nothing here is stored until a tick; a set moves
   * out of this map the moment its tick succeeds, so it is not rendered twice — once from
   * the live document, once from here.
   */
  const [extraSets, setExtraSets] = useState<ReadonlyMap<string, ExerciseSet[]>>(new Map());
  /**
   * The ids of the sets "Add a set" added this session, per catalogId, oldest first. They
   * are shown at the BOTTOM of their card in that order, so the newest is the last row,
   * right above the button that made it (B-62, D-114; B-59 had them on top under a "+").
   * Kept separately from `extraSets` because a set leaves that map the moment it is ticked,
   * and it must not then jump up among the stored sets. Same shape as D-95's card order:
   * a remount forgets it and stored order returns.
   */
  const [addedSetIds, setAddedSetIds] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  /**
   * Rows dismissed by "Remove exercise" while nothing in them is stored (Critical 2, and
   * the finding B fix below it): a suggestion not started, or an exercise added by
   * mistake via the picker. A row with a stored set never reaches this — the button that
   * would add it here is hidden for exactly that row (`canRemove` on `ExerciseEditor`).
   * Re-picking the same exercise clears it here too, so it is offered again rather than
   * immediately filtered back out.
   */
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  /**
   * The bodyweight today's document was last seen with, latched the first time it is
   * observed (Important 4). `withSetRemoved` deletes the day when its last set goes and
   * `addSet` recreates it from `session?.bodyweightKg ?? profile.currentBodyweightKg` —
   * once the document is gone, `session` is null, and without this a day the bot or
   * `/day/:date` had set to some other bodyweight would come back at whatever the
   * profile's CURRENT figure happens to be, scoring every set on it against a number the
   * user never entered for today. Latched once, not kept in sync with later edits: a
   * bodyweight changed by hand after the latch is a case this does not cover.
   */
  const bodyweightRef = useRef<number | null>(null);
  /**
   * The suggestion, held rather than re-derived on every render.
   *
   * `workouts.data` is a new array on every subscription push — including after every
   * tick — so deriving the suggestion from it directly (as a `useMemo` keyed on
   * `workouts.data`) would, once a set is ticked, start copying sets from TODAY's own
   * just-written exercise, since that becomes the most recent occurrence. A suggestion is
   * a proposal made once, at the start of a session, and must not shift under the user
   * while they log against it — so it is computed only when `workouts.data` first
   * arrives, and again only when `today` itself changes.
   *
   * Proposed set ids are now deterministic (`` `proposed:${catalogId}:${index}` ``,
   * B-35), so this is no longer what keeps a still-proposed set's id stable across a
   * remount — a fresh `todaySuggestion` call from scratch lands on the same ids. It is
   * kept for its other, still-load-bearing purpose: without it, tabbing away and back
   * would re-copy from history on every mount, and any tick in between would suddenly be
   * ranked, capped or sorted differently as `workouts.data` grew — the proposal shifting
   * under the user mid-session.
   */
  const held = useRef<{ today: string; value: TodaySuggestion } | null>(null);
  /**
   * Where the page should scroll once a pick closes the picker (B-57): the top, where the
   * new card is sorted (D-95). Applied by the effect below after the render that closes
   * the picker, not in the handler, so it lands on the list as it will actually be laid
   * out. A cancel needs nothing: the cards stay mounted under the sheet, so the page is
   * still on the card it was on.
   */
  const scrollAfterPick = useRef<number | null>(null);

  /*
   * Every hook in this component sits above the early returns further down. `tsc` cannot
   * see a hook placed after one; the running page crashes instead ("Rendered more hooks
   * than during the previous render") — the single most repeated defect in this project.
   */

  /*
   * Snapping is scoped to this page (B-57, D-108). The scroller is the root, which every
   * screen shares, so the class goes on while Today is mounted and comes off on the way
   * out — Progress, Profile and the day editor must scroll freely.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("today-snap");
    return () => {
      root.classList.remove("today-snap");
      root.style.removeProperty("--today-bar-h");
    };
  }, []);

  /*
   * Snapping is lifted for the jump and put straight back. Left on, Chrome later re-snaps
   * to the card it was snapped to before the pick — which the insertion has just pushed
   * down a place — and the page landed on the second card in six of eight picks in the
   * hand check. Put back at 0, the nearest snap position is the new first card, so that
   * is what becomes the snapped target.
   */
  useEffect(() => {
    if (scrollAfterPick.current === null) return;
    const top = scrollAfterPick.current;
    scrollAfterPick.current = null;
    const root = document.documentElement;
    root.classList.remove("today-snap");
    window.scrollTo(0, top);
    // Reading a layout property makes the jump happen before snapping comes back.
    void root.scrollTop;
    root.classList.add("today-snap");
  });

  /**
   * The pinned bar's real height, published on the root for the snapport's top padding.
   * Measured rather than assumed because a save failure adds a second line to it: with a
   * fixed figure every card would snap with its heading under that line. (The cards' own
   * height deliberately does not follow it — see `.today-card` in styles.css.)
   * A callback ref because the bar only exists once the list renders — it mounts after the
   * loading screen, and again after the empty-day screen gains a row.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const barRef = useCallback((bar: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!bar) return;
    const publish = () =>
      document.documentElement.style.setProperty("--today-bar-h", `${bar.getBoundingClientRect().height}px`);
    publish();
    observer.current = new ResizeObserver(publish);
    observer.current.observe(bar);
  }, []);

  const timezone = profile.data?.timezone ?? "UTC";
  const today = localToday(timezone);
  const session = workouts.data?.find((w) => w.date === today) ?? null;

  // Waits for the plan as well as the log: deciding before the plan arrives would hold
  // "last Monday" for the whole session on an account that has a plan.
  if (workouts.data && !plan.loading && held.current?.today !== today) {
    held.current = {
      today,
      value: todaySuggestion(plan.data, workouts.data, today, (catalogId, i) => `proposed:${catalogId}:${i}`),
    };
    // A new `today` means a new day's latch too — otherwise a session observed after
    // `profile.data` arrives (or one left open across midnight) could keep yesterday's
    // bodyweight, or UTC-yesterday's, pinned against a document it no longer describes.
    bodyweightRef.current = null;
  }
  const offered: TodaySuggestion = held.current?.value ?? NOTHING_OFFERED;
  const suggestion = useMemo(() => suggestedExercises(offered), [offered]);
  const rows = useMemo(() => mergeToday(session, suggestion), [session, suggestion]);

  // Re-latch whenever today's document is present, so the ref always holds the
  // bodyweight the day was last SEEN with rather than only the first-observed one — a
  // live change to today's bodyweight (the bot, or `/day/:date`) must not be shadowed.
  // It still survives the document's deletion: `withSetRemoved` deletes the day when its
  // last set goes, `session` goes null, and the ref simply stops being written until the
  // day exists again.
  if (session !== null) {
    bodyweightRef.current = session.bodyweightKg;
  }
  const bodyweightKg = bodyweightRef.current ?? profile.data?.currentBodyweightKg ?? DEFAULT_BODYWEIGHT_KG;

  const toggle = async (row: TodayRow, setId: string, next: boolean) => {
    const found = row.sets.find((s) => s.set.id === setId);
    if (!found) return;
    setFailure(null);
    const result = next
      ? await addSet(uid, today, bodyweightKg, { catalogId: row.catalogId, name: row.name, muscleGroup: row.muscleGroup }, found.set)
      : await removeSet(uid, today, setId);
    // The list is driven by the live subscription, so a success needs no local update —
    // the document changing is what re-renders the row. Only a failure needs saying.
    if (!result.ok) {
      setFailure(result.detail);
      return;
    }
    // Now stored (or removed); the local edit, if any, has done its job.
    setEdits((m) => {
      if (!m.has(setId)) return m;
      const next = new Map(m);
      next.delete(setId);
      return next;
    });
    // A locally-added set that just got ticked is now carried by the live document —
    // drop it here so it is not rendered twice.
    if (next) {
      setExtraSets((m) => {
        const extras = m.get(row.catalogId);
        if (!extras || !extras.some((s) => s.id === setId)) return m;
        const kept = extras.filter((s) => s.id !== setId);
        const nextMap = new Map(m);
        if (kept.length > 0) nextMap.set(row.catalogId, kept);
        else nextMap.delete(row.catalogId);
        return nextMap;
      });
    }
  };

  const changePt = async (next: boolean) => {
    setFailure(null);
    setPtBusy(true);
    const result = await setPt(uid, today, bodyweightKg, next);
    setPtBusy(false);
    if (!result.ok) setFailure(result.detail);
  };
  const ptSwitch = <PtSwitch on={session?.pt === true} busy={ptBusy} onChange={(next) => void changePt(next)} />;
  const ptSwitchCompact = (
    <PtSwitch compact on={session?.pt === true} busy={ptBusy} onChange={(next) => void changePt(next)} />
  );

  const onEdit = (row: TodayRow, next: LoggedExercise) => {
    for (const set of next.sets) {
      const before = row.sets.find((s) => s.set.id === set.id);
      if (!before) continue;
      if (JSON.stringify(before.set) === JSON.stringify(set)) continue;
      if (before.done) {
        // Already stored: the document is the truth, so write it.
        void updateSet(uid, today, set).then((r) => {
          if (!r.ok) setFailure(r.detail);
        });
      } else {
        // Only proposed: hold it locally. Nothing is written until it is ticked.
        setEdits((m) => new Map(m).set(set.id, set));
      }
    }
  };

  /**
   * "Remove exercise", reachable only for a row with nothing stored (`ExerciseEditor`
   * hides the button otherwise — Critical 2). So this only ever drops a proposal: it
   * stops being offered, and is never a Firestore write.
   *
   * Finding B: an added row's set carries a deterministic id (`added-${catalogId}`), so
   * without this, correcting a set and then removing and re-picking the same exercise
   * would resurrect the stale correction on the new row. Pruning `edits` and `extraSets`
   * here, for every set id this row is currently showing, closes that.
   */
  const removeExercise = (row: TodayRow) => {
    setDismissed((d) => (d.has(row.catalogId) ? d : new Set(d).add(row.catalogId)));
    setAdded((a) => a.filter((e) => e.id !== row.catalogId));
    setExtraSets((m) => {
      if (!m.has(row.catalogId)) return m;
      const next = new Map(m);
      next.delete(row.catalogId);
      return next;
    });
    setAddedSetIds((m) => {
      if (!m.has(row.catalogId)) return m;
      const next = new Map(m);
      next.delete(row.catalogId);
      return next;
    });
    setEdits((m) => {
      const ids = new Set(row.sets.map((s) => s.set.id));
      let changed = false;
      const next = new Map(m);
      for (const id of m.keys()) {
        if (ids.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : m;
    });
  };

  if (workouts.error) {
    return (
      <div className="notice bad">
        <b>Couldn't load your log.</b>
        <p className="sub">{workouts.error}</p>
      </div>
    );
  }

  if (workouts.loading || !workouts.data || plan.loading) return <p className="sub">Loading today…</p>;

  const pickedAt = added.map((e) => e.id);
  const pickRank = (catalogId: string) => {
    const i = pickedAt.lastIndexOf(catalogId);
    return i === -1 ? pickedAt.length + 1 : pickedAt.length - i;
  };

  const existingIds = new Set(rows.map((r) => r.catalogId));
  const addedRows: TodayRow[] = added
    .filter((exercise) => !existingIds.has(exercise.id))
    .map((exercise) => ({
      catalogId: exercise.id,
      name: exercise.name,
      muscleGroup: exercise.muscleGroup,
      logged: null,
      sets: [
        {
          set: {
            id: `added-${exercise.id}`,
            reps: exercise.measure === "duration" ? 60 : 10,
            weightKg: 0,
            supported: false,
          },
          done: false,
        },
      ],
      from: null,
    }));

  // Locally-added sets (Important 3 part 2) and the edits overlay both apply here, once,
  // so every consumer below — the editor and the tick handler alike — sees the same set
  // a half-typed number was just entered into, and "Add a set" rows show up everywhere
  // the row itself does.
  const displayRows: TodayRow[] = [...rows, ...addedRows]
    .map((row) => {
      const extras = (extraSets.get(row.catalogId) ?? []).map((set) => ({ set, done: false }));
      const all = [...row.sets, ...extras].map((s) => ({ ...s, set: edits.get(s.set.id) ?? s.set }));
      // Sets "Add a set" added go last, oldest first, so the newest is the bottom row
      // (B-62); everything else keeps its order. Looked up by id, so a ticked one stays
      // put and an unticked-and-deleted one just stops being found.
      const order = addedSetIds.get(row.catalogId) ?? [];
      const rank = (id: string) => order.indexOf(id);
      const sets = all
        .map((s, i) => ({ s, i }))
        .sort((a, b) => rank(a.s.set.id) - rank(b.s.set.id) || a.i - b.i)
        .map(({ s }) => s);
      return { ...row, sets };
    })
    // Critical 2's sibling: a dismissed row stays hidden only while nothing in it is
    // stored. If it later gains a stored set (the bot logs it, say) it reappears — the
    // dismissal was never a statement about that.
    .filter((row) => row.logged !== null || !dismissed.has(row.catalogId))
    /*
     * Picked this session goes first, newest on top (D-95): the button is above the list
     * now, and a card appended below five suggestions landed ~2,600px from the tap that
     * made it. Ranked by `added` rather than by `logged === null`, so the card keeps its
     * place when its first tick stores it — otherwise it would jump to the stored rows'
     * position under the thumb. A remount forgets `added` and stored order returns.
     */
    .sort((a, b) => pickRank(a.catalogId) - pickRank(b.catalogId));

  // With the picker open, the screen below renders it even with nothing else on it.
  if (rows.length === 0 && addedRows.length === 0 && !picking) {
    // The switch and its failure go INSIDE `Nothing`, under the title: `.empty-today` is
    // sized to be the whole screen, so a sibling above it made the page scroll by the
    // switch's 44px and put a checkbox above the statement it qualifies (D-86's shape).
    return (
      <Nothing restDay={offered.kind === "restDay"} onLog={() => setPicking(true)}>
        {ptSwitch}
        {failure && (
          <div className="notice bad">
            <b>That didn't save.</b>
            <p className="sub">{failure}</p>
          </div>
        )}
      </Nothing>
    );
  }

  // Headline or suggestion note turns on whether today has a stored SET, not on whether
  // the document exists: a PT day with nothing logged is a document with no exercises
  // (D-107), and `Headline` must never read "0" over an unlogged screen (B-56).
  const logged = session !== null && session.exercises.some((e) => e.sets.length > 0) ? session : null;

  return (
    <>
      {/* Pinned (B-57): what the day is so far, the PT switch and "+ Add", on one line that
          stays put while the cards move under it. A save failure is its second line, so it
          cannot scroll away with the card that caused it. */}
      <div className="today-bar" ref={barRef}>
        {/* The bar took the visible "Today" heading's place; a screen reader still needs one. */}
        <h1 className="visually-hidden">Today</h1>
        <div className="today-bar-line">
          <p className="today-bar-summary">
            {logged !== null ? <Headline session={logged} /> : <ShortSuggestion offered={offered} />}
          </p>
          {ptSwitchCompact}
          <button className="btn btn-small" type="button" onClick={() => setPicking(true)}>
            + Add
          </button>
        </div>
        {failure && (
          <p className="today-bar-fault" role="alert">
            <b>That didn't save.</b> <span className="sub">{failure}</span>
          </p>
        )}
      </div>

      {/* A full-screen sheet over the cards (it is `position: fixed`), so the cards stay
          mounted under it and a cancel returns to the card that was on screen. */}
      {picking && (
        <ExercisePicker
          onPick={(exercise) => {
            setPicking(false);
            setAdded((a) => [...a, exercise]);
            // The new card is sorted first (D-95); land on it.
            scrollAfterPick.current = 0;
            // Re-picking a dismissed exercise should offer it again, not have the
            // dismissal filter it straight back out.
            setDismissed((d) => {
              if (!d.has(exercise.id)) return d;
              const next = new Set(d);
              next.delete(exercise.id);
              return next;
            });
          }}
          onCancel={() => setPicking(false)}
        />
      )}

      {displayRows.map((row, index) => (
        <div className="today-card" key={row.logged?.id ?? row.catalogId}>
          <ExerciseEditor
            exercise={{
              id: row.logged?.id ?? row.catalogId,
              catalogId: row.catalogId,
              name: row.name,
              muscleGroup: row.muscleGroup,
              sets: row.sets.map((s) => s.set),
            }}
            catalog={exerciseById(row.catalogId) ?? UNKNOWN_EXERCISE}
            bodyweightKg={bodyweightKg}
            done={new Map(row.sets.map((s) => [s.set.id, s.done]))}
            onToggleDone={(setId, next) => void toggle(row, setId, next)}
            onChange={(next) => onEdit(row, next)}
            onRemove={() => removeExercise(row)}
            canRemove={row.logged === null}
            position={{ index: index + 1, count: displayRows.length }}
            onAddSet={(set) => {
              setExtraSets((m) => {
                const next = new Map(m);
                next.set(row.catalogId, [...(m.get(row.catalogId) ?? []), set]);
                return next;
              });
              setAddedSetIds((m) => new Map(m).set(row.catalogId, [...(m.get(row.catalogId) ?? []), set.id]));
            }}
            onRemoveLocalSet={(setId) =>
              setExtraSets((m) => {
                const extras = m.get(row.catalogId);
                if (!extras) return m;
                const kept = extras.filter((s) => s.id !== setId);
                const next = new Map(m);
                if (kept.length > 0) next.set(row.catalogId, kept);
                else next.delete(row.catalogId);
                return next;
              })
            }
            localSetIds={new Set((extraSets.get(row.catalogId) ?? []).map((s) => s.id))}
          />
        </div>
      ))}

      {/* Below the last card and not a card itself: where the rows came from, and the
          offer to make a plan. It was the first thing on the screen; now it is the last. */}
      {(offered.kind === "plan" || offered.kind === "lastWeekday") && (
        <footer className="today-foot">
          <SuggestionNote today={today} offered={offered} />
        </footer>
      )}
    </>
  );
}

/**
 * What has happened today: the score so far, and how much of it there is.
 *
 * Only rendered once something is stored, so it never reads "0" over a screen whose own
 * first line already says nothing is logged. It counts what is IN the document — a
 * ticked set — and not the proposals below it, which are a suggestion and not a claim
 * about the day.
 */
function Headline({ session }: { session: Workout }) {
  const sets = session.exercises.reduce((n, e) => n + e.sets.length, 0);

  return (
    <>
      <b className="num">{formatNumber(scoreWorkout(session))}</b>{" "}
      <span className="sub">
        · {session.exercises.length} {plural(session.exercises.length, "exercise")} · {sets} {plural(sets, "set")}
      </span>
    </>
  );
}

/**
 * The bar's short form of the suggestion (B-57), for before anything is stored. The whole
 * sentence is in the footer below the last card; this only has to say where the cards
 * came from in a few words.
 */
function ShortSuggestion({ offered }: { offered: TodaySuggestion }) {
  if (offered.kind === "plan") return <span className="sub">{offered.label} · plan</span>;
  if (offered.kind !== "lastWeekday") return null;
  // `offered.from` is a calendar date, so its weekday comes from core's `dayOfWeek`
  // rather than a Date object (see the note on SuggestionNote below).
  const weekday = WEEKDAYS[dayOfWeek(offered.from)] ?? "";
  return <span className="sub">From last {weekday.slice(0, 3)}</span>;
}

/*
 * The weekday name is indexed from core's `dayOfWeek` rather than asked of `Intl`. Core
 * does not know about locales and this line does not need it to — the app is one
 * person's training log, in English, and an Intl call here would be the only one outside
 * functions/src/time.ts.
 */
function SuggestionNote({ today, offered }: { today: string; offered: TodaySuggestion }) {
  if (offered.kind === "plan") {
    return (
      <p className="sub suggestion-note">
        <b>{offered.label} · from your plan.</b> Weights are from the last time you did each one, so check
        them before you tick.
      </p>
    );
  }
  if (offered.kind !== "lastWeekday") return null;
  const [, month, day] = offered.from.split("-").map(Number) as [number, number, number];
  return (
    <>
      <p className="sub suggestion-note">
        <b>
          What you did last {WEEKDAYS[dayOfWeek(today)]}, {day} {MONTHS[month - 1]}.
        </b>{" "}
        Check the numbers before you tick.
      </p>
      <p className="sub plan-nudge">
        Want a plan built for you? <Link to="/plan/new">Create one</Link>
      </p>
    </>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "PT session today" (B-56). It reads the stored document, not local state, so what it
 * shows is what is saved; it is disabled while its own write is in flight. Not `--live`:
 * a PT day is none of the four things that token marks (D-63).
 */
function PtSwitch({
  on,
  busy,
  onChange,
  compact = false,
}: {
  on: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
  /** The bar's form (B-57): "PT" on screen, the whole name for a screen reader. */
  compact?: boolean;
}) {
  return (
    <label className={compact ? "check check-compact" : "check"}>
      <input
        type="checkbox"
        checked={on}
        disabled={busy}
        aria-label={compact ? "PT session today" : undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span aria-hidden={compact || undefined}>{compact ? "PT" : "PT session today"}</span>
    </label>
  );
}

/**
 * Nothing logged today, and nothing to offer (B-49): either this weekday has never been
 * trained and there is no plan, or the plan says today is a rest day.
 *
 * Two ways forward, in the order the spec gives them: make a plan, or just log. "Just
 * log today" opens the exercise picker on this screen rather than the day editor — it is
 * the same Add an exercise the screen has when it has rows, so ticking a set here works
 * the way it does everywhere else on Today. The note about the bot stays at the foot,
 * with the example in the `reps @ weight` form the parser learned in B-31.
 *
 * `children` (the PT switch and its failure, B-56) sit under the title, inside the
 * column: this section fills the screen, so anything beside it would add scroll.
 */
function Nothing({ restDay, onLog, children }: { restDay: boolean; onLog: () => void; children?: ReactNode }) {
  return (
    <section className="empty-today" aria-label="Today's session">
      <h1 className="title">{restDay ? "Rest day in your plan." : "Nothing logged today."}</h1>

      {children}

      <p className="empty-today-action">
        {!restDay && (
          <Link className="btn" to="/plan/new">
            Create a plan
          </Link>
        )}
        <button className="btn btn-primary" type="button" onClick={onLog}>
          {restDay ? "Log something anyway" : "Just log today"}
        </button>
      </p>

      <p className="sub empty-today-note">
        You can also log by messaging the bot — <b>bench 12 @ 30kg</b> — and it will appear here as you send it.
      </p>
    </section>
  );
}
